import * as React from "react";
import styled from "styled-components";
import RegularButton from "./RegularButton";
import {IStoreState} from "../../modules/types";
import {getUser, getUserId} from "../../modules/selectors";
import {useSelector} from "react-redux";
import {share} from "../../modules/utils";
import facebook_icon from "../../assets/img/icons/Facebook.svg";
import twitter_icon from "../../assets/img/icons/Twitter.svg";
import share_icon from "../../assets/img/icons/Share.svg";
import share_icon_grey from "../../assets/img/icons/Share-grey.svg";
import theme from "../../assets/css/theme";
import close_icon from "../../assets/img/icons/close_Icon.svg";
import SponsorImage from "../SponsorImage";
import {
	ConnextraType,
	createConnextraScriptTag,
} from "../../modules/utils/Сonnextra/connextra";
import {createFanHubEvent, EventGUID} from "modules/utils/FanHubTagging/next";
import {Exist} from "components/Exist";
import {ButtonIcon} from "components/Buttons/ButtonIcon";

const GAME_NAME = process.env.REACT_APP_WEBSITE_NAME;

const ShareButtonWrapper = styled(RegularButton)`
	background-image: url("${share_icon}");
	background-repeat: no-repeat;
	background-position: center left 15px;
	max-width: 220px;
	height: 40px;
	padding: 0 20px 0 40px;
	margin: 0 auto;
	position: relative;
	font-size: 12px;
	white-space: nowrap;
	text-overflow: ellipsis;
	overflow: hidden;
`;

const CloseIcon = styled.div`
	background: url("${close_icon}") center no-repeat;
	width: 24px;
	height: 24px;
	position: absolute;
	right: 0;
	top: -30px;
	cursor: pointer;
`;

const ShareIcon = styled.div`
	min-width: 40px;
	height: 40px;
	background-repeat: no-repeat;
	background-position: center center;
	background-size: 100% 100%;
`;

const FacebookShare = styled(ShareIcon)`
	background-image: url("${facebook_icon}");
	width: 50px;
	height: 50px;
	cursor: pointer;
`;

const TwitterShare = styled(ShareIcon)`
	background-image: url("${twitter_icon}");
	width: 50px;
	height: 50px;
	cursor: pointer;
`;

const Share = styled.img`
	margin-top: 5px;
	width: 20px;
	margin-bottom: 10px;
`;

const SocialsShare = styled.div`
	border-radius: 4px;
	max-width: 500px;
	width: 100%;
	height: 276px;
	display: flex;
	flex-flow: column;
	align-items: center;
	justify-content: center;
	background-color: #fff;
	z-index: 2;
	position: relative;
	left: 50%;
	transform: translateX(-50%) translateY(-50%);
	top: 50%;
	box-sizing: border-box;
	padding: 20px;
`;

const SocialShareText = styled.div`
	color: #333;
	letter-spacing: -0.1px;
	line-height: 20px;
	font-size: 16px;
	font-family: ${theme.base_bold_font};
	max-width: 300px;
	word-break: break-word;
	text-align: center;
	padding-bottom: 10px;
`;

const InconsWrapper = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-top: 20px;
	${FacebookShare} {
		margin-right: 20px;
	}
`;

const ShareLayout = styled.div`
	background-color: rgba(0, 0, 0, 0.5);
	width: 100%;
	height: 100%;
	position: fixed;
	z-index: 1;
	left: 0;
	top: 0;
`;

interface IProps {
	children?: string;
	disabled?: boolean;
	actual_gameweek?: number;
	section: "league" | "general" | "predictions" | "results";
	actual_gameweek_status?: "scheduled" | "active" | "complete";
	score?: number;
	gamebar?: boolean;
}

const ShareButtonComponent: React.FC<IProps> = ({
	children,
	disabled,
	actual_gameweek,
	section,
	actual_gameweek_status,
	score,
	gamebar,
}) => {
	const [socialsShown, setSocialsShown] = React.useState(false);
	const user_id = useSelector((state: IStoreState) => getUserId(state));
	const user = useSelector((state: IStoreState) => getUser(state));

	const showSocials = React.useCallback(() => {
		if (window._satellite) {
			window._satellite.track("showSocials");
		}
		createConnextraScriptTag(ConnextraType.SHARE_CLICK, user);
		setSocialsShown(true);
	}, [user]);

	const hideSocials = React.useCallback(() => {
		setSocialsShown(false);
	}, []);

	const handleShare = React.useCallback(
		(sc: "tw" | "fb") => {
			let message = "";

			if (actual_gameweek_status) {
				if (actual_gameweek_status !== "complete") {
					message = `${window.getTranslations(
						"pickem_share_twitter_picks_ive_entered"
					)} ${GAME_NAME} ${window.getTranslations(
						"pickem_share_twitter_picks_picks_for"
					)} ${actual_gameweek}! ${window.getTranslations(
						"pickem_share_twitter_picks_check_out"
					)}!`;
				} else {
					message = `${window.getTranslations(
						"pickem_share_twitter_results_nfl_pickem"
					)} ${actual_gameweek} ${window.getTranslations(
						"pickem_share_twitter_results_is_over"
					)} ${score}! ${window.getTranslations(
						"pickem_share_twitter_results_check_out"
					)}!`;
				}
			} else {
				message = `${window.getTranslations(
					"social_button_twitter_pick_page"
				)}`;
			}
			if (window._satellite) {
				window._satellite.track("socialShare", {
					platform: sc,
				});
			}

			createFanHubEvent(EventGUID.GAMEPLAY_SHARE);

			createConnextraScriptTag(
				sc === "fb"
					? ConnextraType.SHARE_CLICK_FACEBOOK
					: ConnextraType.SHARE_CLICK_TWITTER,
				user
			);

			share({
				sc,
				section,
				message,
				user_id,
				round_id: actual_gameweek,
			});
		},
		[actual_gameweek_status, actual_gameweek, score, section, user_id, user]
	);

	const handleFacebookShare = React.useCallback(
		() => handleShare("fb"),
		[handleShare]
	);

	const handleTwitterShare = React.useCallback(
		() => handleShare("tw"),
		[handleShare]
	);

	return (
		<React.Fragment>
			<Exist when={!!gamebar}>
				<ButtonIcon className={"icon"} onClick={showSocials}>
					<img src={share_icon} alt="Share" />
				</ButtonIcon>
			</Exist>
			<Exist when={!gamebar}>
				<ShareButtonWrapper
					aria-label="share"
					disabled={disabled}
					onClick={showSocials}
				>
					{children}
				</ShareButtonWrapper>
			</Exist>
			{socialsShown && (
				<ShareLayout>
					<SocialsShare>
						<CloseIcon onClick={hideSocials} />
						<Share src={share_icon_grey} role="presentation" />
						<SocialShareText>
							{window.getTranslations("social_modal_popup_modal")}
							!
						</SocialShareText>
						<SponsorImage
							className="less-margin"
							landing={true}
							position="share"
						/>
						<InconsWrapper>
							<FacebookShare onClick={handleFacebookShare} />
							<TwitterShare onClick={handleTwitterShare} />
						</InconsWrapper>
					</SocialsShare>
				</ShareLayout>
			)}
		</React.Fragment>
	);
};

export default ShareButtonComponent;
